import { getApi } from "../Services/apiService";
import { calculateAge, getMemberDobTimestamp } from "./family-tree/dateAndTime";

/**
 * The "Descendant Age Group" report — GET
 * /report-management/descendants-by-age-category.
 *
 * The endpoint does the split: the living descendants (generation level > 1,
 * no spouses, no deceased) in two groups, those 17 and younger and those 18 and
 * above, each group carrying its own count and its member rows — name parts,
 * family-line colour and the head of the line. Both the dashboard card's pair
 * of counts and the detail page's two columns come through here, so the two
 * can never show different numbers.
 *
 * Sample payload:
 *   { family_tree_id, year: 2026, total_descendants: 45,
 *     descendants_aged_1_to_17: { title: "Descendants Aged 1–17 Years",
 *       age_range: "1-17", total_count: 15, members: [ {...} ] },
 *     descendants_aged_18_and_above: { title: "Descendants Aged 18 Years and Above",
 *       age_range: "18+", total_count: 30, members: [ {...} ] } }
 */

/** Whole number or null — the card and the heading print 0 for null. */
const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/** One of the payload's two group objects → the group the page and card read. */
function toAgeGroup(group) {
  const members = Array.isArray(group?.members)
    ? group.members.filter((member) => member && typeof member === "object")
    : [];

  return {
    title: String(group?.title ?? "").trim(),
    ageRange: String(group?.age_range ?? "").trim(),
    count: toNumberOrNull(group?.total_count) ?? members.length,
    members,
  };
}

const EMPTY_REPORT = {
  year: null,
  totalCount: 0,
  minors: toAgeGroup(null),
  adults: toAgeGroup(null),
};

/**
 * The two groups plus the grand total the heading prints. `totalCount` and
 * each group's `count` are the API's own; the rows are only counted when the
 * API leaves the number out.
 */
export async function fetchDescendantsByAgeCategory(familyTreeId) {
  if (!familyTreeId) return EMPTY_REPORT;

  const response = await getApi(
    `/report-management/descendants-by-age-category?family_tree_id=${familyTreeId}`
  );

  if (response?.statusCode !== 200 || !response?.data) return EMPTY_REPORT;

  const minors = toAgeGroup(response.data.descendants_aged_1_to_17);
  const adults = toAgeGroup(response.data.descendants_aged_18_and_above);

  return {
    year: toNumberOrNull(response.data.year),
    totalCount:
      toNumberOrNull(response.data.total_descendants) ??
      minors.count + adults.count,
    minors,
    adults,
  };
}

/**
 * Full years old, for the page's Age Range filter: the report row's own `age`
 * — the number the endpoint split the two groups by — else counted today off
 * the record's date of birth; null when neither is known, and such a row is
 * left out of any age range.
 */
export function getDescAgeGroupMemberAge(member) {
  const reported = toNumberOrNull(member?.age);
  if (reported != null) return reported;

  const dobMs = getMemberDobTimestamp(member);
  if (dobMs === -1) return null;
  return toNumberOrNull(calculateAge(new Date(dobMs).toISOString().slice(0, 10)));
}

/**
 * The members whose age falls inside the range, either bound optional: `from`
 * alone keeps everyone that old and older, `to` alone everyone that young and
 * younger. No bounds returns the list as given.
 */
export function filterDescAgeGroupMembersByAge(members, { from = null, to = null } = {}) {
  const list = Array.isArray(members) ? members : [];
  if (from == null && to == null) return list;

  return list.filter((member) => {
    const age = getDescAgeGroupMemberAge(member);
    if (age == null) return false;
    if (from != null && age < from) return false;
    if (to != null && age > to) return false;
    return true;
  });
}

/** A statistics entry that is either the bare number or the report's group object. */
const toGroupCount = (value) =>
  toNumberOrNull(typeof value === "object" && value !== null ? value.total_count : value);

/**
 * The pair the dashboard card prints — 17 and younger over 18 and older, like
 * the Males/Females card's "127/94".
 *
 * Read off the dashboard statistics block's own two counts,
 * `descendants_aged_1_to_17_count` and `descendants_aged_18_and_above_count`
 * (or the report's keys, bare or as group objects, where a payload still
 * names those instead), else off the report endpoint's own totals, which the
 * dashboard fetches for the card the way it does the Generations & Ages
 * total. Null until either has landed.
 */
export function getDescAgeGroupCounts(statistics, report) {
  const fromStatistics = {
    minors:
      toNumberOrNull(statistics?.descendants_aged_1_to_17_count) ??
      toGroupCount(statistics?.descendants_aged_1_to_17),
    adults:
      toNumberOrNull(statistics?.descendants_aged_18_and_above_count) ??
      toGroupCount(statistics?.descendants_aged_18_and_above),
  };
  if (fromStatistics.minors != null && fromStatistics.adults != null) {
    return fromStatistics;
  }

  if (!report) return null;

  return {
    minors: report.minors?.count ?? 0,
    adults: report.adults?.count ?? 0,
  };
}

/** Gender codes the page's Gender filter keys on: male, female, or Binary/Other. */
export const DESC_AGE_GROUP_GENDERS = { MALE: "M", FEMALE: "F", OTHER: "O" };

/**
 * "M", "F" or "O" (the member forms' Binary and Other, codes 3 and 4) off the
 * record's own gender, else the roster's id → "M"/"F" map (see buildGenderMap);
 * null when neither knows, and such a row is left out of any gender filter.
 */
export function getDescAgeGroupMemberGender(member, genderById) {
  const raw = member?.gender ?? member?.Gender ?? member?.member_gender ?? member?.sex;
  const value = raw == null ? "" : String(raw).trim().toLowerCase();
  if (value === "1" || value === "male" || value === "m") return DESC_AGE_GROUP_GENDERS.MALE;
  if (value === "2" || value === "female" || value === "f") return DESC_AGE_GROUP_GENDERS.FEMALE;
  if (value === "3" || value === "4" || value === "binary" || value === "other") {
    return DESC_AGE_GROUP_GENDERS.OTHER;
  }

  const id = [member?.id, member?.family_member_id, member?.member_id].find(
    (candidate) => candidate != null && candidate !== ""
  );
  return (id != null && genderById?.get(String(id))) || null;
}
